import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useFilterContext } from "../../../state/FilterContext";
import { useArrayContext } from "../../../state/ArrayContext";
import { useSiteContext } from "../../../state/SiteContext";
import { services } from "../../../globals";
import { MetadataRequest } from "../../../shared/models/metadata-request.model";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";

interface ValidityValuesQueryProps {
    validityPathArg?: string | null,
    children?: ReactNode,
}

/***
* Returns all existing values of a validity property, after applying the current filters.
 * The validity property is removed from the filters before the search is performed.
 * A validity path is used to specify the property. The path is a colon-separated list of
 * property names, of to but not including the property to get the values for.
 * 
 * For example, assume the validity definition is:
 * {
 *    name: "division", values: {
 *       "appliances": {
 *          name: "product", values: {}
 *       }
 *   }
 * }
 * 
 * If the specification is "", the values returned will be all values of the property "division".
 * 
 * If the specification is "appliances", the values returned will be all values of the
 * property "product".
 * 
 * Values starting with a dollar sign ($) are not included in the result.
  */
export const ValidityValuesQuery = React.memo(function ValidityValuesQuery({ validityPathArg, children }: ValidityValuesQueryProps): React.ReactElement {
    const { setArray } = useArrayContext();
    const { siteState } = useSiteContext();
    const { filterState } = useFilterContext();

    // Get validity path from the validity child unless it was provided as a prop
    const [validityPath, setValidityPath] = useState<string | null>(null);
    const validityChild = useMemo(() => validityPathArg ? null : findFirstComponentOfType(children, "arg-validity"), [children, validityPathArg]);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            const usedValidityPath = validityPathArg ?? validityPath;
            if (siteState.site?.name && usedValidityPath !== null) {
                const request: MetadataRequest = { ...filterState };
                request.validity = "";

                const valueItems = await services.search.getPropertyValueSet(
                    siteState.site.name || "",
                    request,
                    usedValidityPath,
                    true,
                    false,
                );
                const result = valueItems.filter((item, _ix) => { return !item.value.toString().startsWith("$") }).map((item, ix) => {
                    return { ...item, position: ix + 1 };
                });
                if (isMounted && usedValidityPath !== null) {
                    setArray(result);
                }
            }
        };
        fetchData();
        return () => { isMounted = false };
    }, [siteState, filterState, setArray, validityPath, validityPathArg]);

    return <>
        {validityChild && (
            <TextExtractor onExtractedText={setValidityPath}>
                {validityChild?.props.children}
            </TextExtractor>
        )}
    </>;
});